import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  MessageCircle,
  Send,
  Loader2,
  Sparkles,
  TreeDeciduous,
  Bug,
  Droplets,
  Leaf,
  HelpCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const suggestedQuestions = [
  {
    icon: Bug,
    question: "Minha árvore tem manchas nas folhas, o que pode ser?",
    color: "text-red-500"
  },
  {
    icon: Droplets,
    question: "Com que frequência devo irrigar meu Ipê?",
    color: "text-blue-500"
  },
  {
    icon: Leaf,
    question: "Qual a melhor época para podar árvores frutíferas?",
    color: "text-green-500"
  },
  {
    icon: TreeDeciduous,
    question: "Como identificar se uma árvore está doente?",
    color: "text-amber-500"
  }
];

const ConsultaIAPage = () => {
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversation, setConversation] = useState([]);

  const handleSubmit = async (questionText) => {
    const q = questionText || question;
    if (!q.trim()) {
      toast.error("Digite uma pergunta");
      return;
    }

    setLoading(true);
    const userMessage = { role: "user", content: q };
    setConversation((prev) => [...prev, userMessage]);
    setQuestion("");

    try {
      const response = await axios.post(
        `${API}/ai/consult`,
        { question: q },
        { withCredentials: true }
      );
      
      const aiMessage = { role: "assistant", content: response.data.response };
      setConversation((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error("Error consulting AI:", error);
      toast.error("Erro ao consultar IA. Tente novamente.");
      // Remove the user message if there was an error
      setConversation((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestedQuestion = (q) => {
    setQuestion(q);
    handleSubmit(q);
  };

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 animate-fade-in">
          <h1 className="text-2xl md:text-3xl font-bold font-['Outfit'] mb-2 flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-primary" />
            Consulta com IA
          </h1>
          <p className="text-muted-foreground">
            Tire suas dúvidas sobre árvores, pragas, doenças e cuidados com a ajuda da inteligência artificial
          </p>
        </div>

        {/* Suggested Questions */}
        {conversation.length === 0 && (
          <div className="mb-8 animate-fade-in stagger-1">
            <p className="text-sm text-muted-foreground mb-4">Perguntas sugeridas:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {suggestedQuestions.map((item, index) => (
                <Button
                  key={index}
                  variant="outline"
                  className="h-auto p-4 justify-start text-left hover:bg-primary/5 hover:border-primary/30"
                  onClick={() => handleSuggestedQuestion(item.question)}
                >
                  <item.icon className={`w-5 h-5 mr-3 flex-shrink-0 ${item.color}`} />
                  <span className="text-sm">{item.question}</span>
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Conversation */}
        <Card className="rounded-2xl card-shadow mb-6 animate-fade-in stagger-2">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <MessageCircle className="w-5 h-5 text-primary" />
              Conversa
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="min-h-[300px] max-h-[500px] overflow-y-auto space-y-4 mb-4">
              {conversation.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[300px] text-center">
                  <HelpCircle className="w-16 h-16 text-muted-foreground/20 mb-4" />
                  <p className="text-muted-foreground">
                    Faça uma pergunta sobre suas árvores ou plantas
                  </p>
                  <p className="text-sm text-muted-foreground/70 mt-2">
                    A IA irá ajudá-lo a identificar problemas e soluções
                  </p>
                </div>
              ) : (
                conversation.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[85%] p-4 rounded-2xl ${
                        msg.role === "user"
                          ? "bg-primary text-white rounded-br-md"
                          : "bg-accent rounded-bl-md"
                      }`}
                    >
                      {msg.role === "assistant" && (
                        <div className="flex items-center gap-2 mb-2 text-primary">
                          <Sparkles className="w-4 h-4" />
                          <span className="text-sm font-medium">Mestre Paulo IA</span>
                        </div>
                      )}
                      <div
                        className="text-sm whitespace-pre-wrap leading-relaxed"
                        dangerouslySetInnerHTML={{
                          __html: msg.content
                            .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                            .replace(/\n/g, "<br/>")
                        }}
                      />
                    </div>
                  </div>
                ))
              )}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-accent p-4 rounded-2xl rounded-bl-md">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-primary" />
                      <span className="text-sm text-muted-foreground">Pensando...</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="flex gap-3">
              <Textarea
                data-testid="ai-question-input"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Digite sua pergunta sobre árvores, pragas, doenças..."
                className="min-h-[60px] resize-none"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
              />
              <Button
                data-testid="send-question-button"
                onClick={() => handleSubmit()}
                disabled={loading || !question.trim()}
                className="btn-primary px-6"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tips */}
        <Card className="rounded-2xl bg-primary/5 border-primary/20 animate-fade-in stagger-3">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Leaf className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium text-sm">Dica</p>
                <p className="text-sm text-muted-foreground">
                  Seja específico nas suas perguntas. Inclua detalhes como espécie da árvore, 
                  sintomas observados e há quanto tempo o problema apareceu para obter respostas mais precisas.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ConsultaIAPage;
